import { useEffect, useMemo, useState } from 'react'
import { useCampaigns } from '@/api/campaigns'
import { usePpcCodes, type PpcCode as ApiPpcCode } from '@/api/naming'
import { groupPpcCodesByCategory, type PpcCode as StaticPpcCode } from '@/lib/ppcCodes'
import { Badge } from '@/components/StatusBadge'
import { AlertTriangle } from 'lucide-react'

export type AdType = 'SP' | 'SB' | 'SD'
export type Targeting = 'Auto' | 'KW' | 'PAT' | 'AUD'
export type MatchType = 'Exact' | 'Phrase' | 'Broad'

const NAMING_REGEX =
  /^\d{2}_[A-Z]{2,5}_(SP|SB|SD)_(Auto|KW(_(Exact|Phrase|Broad))?|PAT|AUD)_\w+$/

const KW_STRATEGIES = ['Scale', 'Research', 'Defense', 'Discovery', 'Brand'] as const
const AUTO_STRATEGIES = ['Discovery', 'Research', 'Defense'] as const
const AUD_STRATEGIES = ['Retargeting', 'Lookalike', 'Defense'] as const
const PAT_STRATEGIES = ['Defense', 'Research', 'Brand'] as const

function strategiesFor(targeting: Targeting): readonly string[] {
  if (targeting === 'Auto') return AUTO_STRATEGIES
  if (targeting === 'AUD') return AUD_STRATEGIES
  if (targeting === 'PAT') return PAT_STRATEGIES
  return KW_STRATEGIES
}

function buildName(opts: {
  code: string
  adType: AdType
  targeting: Targeting
  match: MatchType
  strategy: string
}): string {
  const { code, adType, targeting, match, strategy } = opts
  if (targeting === 'Auto') return `${code}_${adType}_Auto_${strategy}`
  if (targeting === 'KW') return `${code}_${adType}_KW_${match}_${strategy}`
  return `${code}_${adType}_${targeting}_${strategy}`
}

export type NamingBuilderState = {
  code: string
  adType: AdType
  targeting: Targeting
  match: MatchType
  strategy: string
  generatedName: string
  schemaValid: boolean
  collision: boolean
  collisionCheckLoading: boolean
  isReady: boolean
}

type Props = {
  onChange?: (state: NamingBuilderState) => void
}

const labelClasses =
  'mb-1 block text-[10px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]'
const selectClasses =
  'w-full rounded-md border border-[var(--border)] bg-[var(--card)] px-2.5 py-1.5 font-mono text-sm text-[var(--foreground)] disabled:opacity-50'

export default function NamingBuilder({ onChange }: Props) {
  const [code, setCode] = useState<string>('05_SCA')
  const [adType, setAdType] = useState<AdType>('SP')
  const [targeting, setTargeting] = useState<Targeting>('KW')
  const [match, setMatch] = useState<MatchType>('Exact')
  const [strategy, setStrategy] = useState<string>('Scale')

  // v35.3 — Backend-Hook ist Single-Source-of-Truth, lib/ppcCodes ist Fallback
  // wenn der Hook noch lädt oder fehlschlägt.
  const ppcQuery = usePpcCodes()
  const grouped = useMemo(() => {
    const apiCodes = ppcQuery.data?.codes
    if (!apiCodes || apiCodes.length === 0) {
      return groupPpcCodesByCategory()
    }
    return apiCodes.reduce<Record<string, ApiPpcCode[]>>((acc, entry) => {
      const key = `${entry.categoryNumber} · ${entry.category}`
      ;(acc[key] ??= []).push(entry)
      return acc
    }, {}) as Record<string, ReadonlyArray<ApiPpcCode | StaticPpcCode>>
  }, [ppcQuery.data])
  const strategies = strategiesFor(targeting)

  // Strategie zuruecksetzen wenn Targeting wechselt und aktuelle Strategie nicht passt.
  useEffect(() => {
    if (!strategies.includes(strategy)) {
      setStrategy(strategies[0]!)
    }
  }, [strategies, strategy])

  const generated = buildName({ code, adType, targeting, match, strategy })
  const schemaValid = NAMING_REGEX.test(generated)

  // Debounced Collision-Check
  const [debouncedName, setDebouncedName] = useState(generated)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(generated), 300)
    return () => clearTimeout(t)
  }, [generated])

  const collisionQuery = useCampaigns({ search: debouncedName, limit: 5 })
  const collisionLoading = collisionQuery.isFetching && debouncedName === generated
  const collisionData = collisionQuery.data?.campaigns ?? []
  const collision =
    !collisionLoading && collisionData.some((c) => c.name === generated)

  const isReady = schemaValid && !collision && !collisionLoading

  // Hochreichen
  useEffect(() => {
    onChange?.({
      code,
      adType,
      targeting,
      match,
      strategy,
      generatedName: generated,
      schemaValid,
      collision,
      collisionCheckLoading: collisionLoading,
      isReady,
    })
  }, [code, adType, targeting, match, strategy, generated, schemaValid, collision, collisionLoading, isReady, onChange])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-5">
        <div>
          <label className={labelClasses}>PPC-Code</label>
          <select
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={selectClasses}
          >
            {Object.entries(grouped).map(([key, codes]) => (
              <optgroup key={key} label={key}>
                {codes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} · {c.lager}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClasses}>Ad-Typ</label>
          <select
            value={adType}
            onChange={(e) => setAdType(e.target.value as AdType)}
            className={selectClasses}
          >
            <option>SP</option>
            <option>SB</option>
            <option>SD</option>
          </select>
        </div>

        <div>
          <label className={labelClasses}>Targeting</label>
          <select
            value={targeting}
            onChange={(e) => setTargeting(e.target.value as Targeting)}
            className={selectClasses}
          >
            <option>Auto</option>
            <option>KW</option>
            <option>PAT</option>
            <option>AUD</option>
          </select>
        </div>

        <div>
          <label className={labelClasses}>Match-Type</label>
          <select
            value={match}
            onChange={(e) => setMatch(e.target.value as MatchType)}
            disabled={targeting !== 'KW'}
            className={selectClasses}
          >
            <option>Exact</option>
            <option>Phrase</option>
            <option>Broad</option>
          </select>
        </div>

        <div>
          <label className={labelClasses}>Strategie</label>
          <select
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            className={selectClasses}
          >
            {strategies.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-[10px] border border-[var(--border)] bg-[var(--muted)]/30 p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
          Generierter Name
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-[18px] font-bold tracking-tight text-[var(--foreground)] break-all">
            {generated}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <Badge tone={schemaValid ? 'good' : 'bad'} size="md">
              {schemaValid ? '✓ Schema konform' : '✗ Schema ungültig'}
            </Badge>
            <Badge
              tone={collisionLoading ? 'neutral' : collision ? 'bad' : 'good'}
              size="md"
            >
              {collisionLoading
                ? '… Bestand prüfen'
                : collision
                  ? '✗ Name existiert bereits'
                  : '✓ Name verfügbar'}
            </Badge>
          </div>
        </div>
        {collision && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              Eine Kampagne mit diesem Namen existiert bereits in der Datenbank. Wähle eine andere
              Strategie- oder Match-Kombination.
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export const NAMING_BUILDER_REGEX = NAMING_REGEX

export const NAMING_EXAMPLES: ReadonlyArray<{ name: string; ok: boolean; note: string }> = [
  { name: '05_SCA_SP_KW_Exact_Scale', ok: true, note: 'SCALA — Exact Keyword, Scale-Strategie' },
  { name: '01_FGR_SP_Auto_Discovery', ok: true, note: 'Fenstergriffe — Auto-Targeting, Discovery' },
  { name: '07_SKM_SD_AUD_Retargeting', ok: true, note: 'Sechskantmuttern — SD Audience-Retargeting' },
  { name: 'Stahlia_KW-Markenschutz', ok: false, note: 'Legacy: kein PPC-Code, Sonderzeichen' },
  { name: '5_SCA_SP_KW_Exact_Scale', ok: false, note: 'PPC-Code muss 2-stellig sein' },
]
